import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { useToast } from '../hooks/use-toast';
import { services, studioHours } from '../mock';
import { CalendarCheck, Send, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build a Date -> studio hours lookup
const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Generate 30-minute slots between "9:00 AM" and "7:00 PM"
const parseHour = (str) => {
  // "9:00 AM" -> minutes since midnight
  const [time, period] = str.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + m;
};

const formatSlot = (mins) => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 === 0 ? 12 : h % 12;
  return `${h}:${m.toString().padStart(2, '0')} ${period}`;
};

const getSlotsForDay = (dayName) => {
  const entry = studioHours.find((s) => s.day === dayName);
  if (!entry || entry.hours.toLowerCase() === 'closed') return [];
  const [openStr, closeStr] = entry.hours.split(' - ');
  const open = parseHour(openStr);
  const close = parseHour(closeStr);
  const slots = [];
  // Last bookable slot 60 min before close so appointment fits
  for (let t = open; t <= close - 60; t += 30) {
    slots.push(formatSlot(t));
  }
  return slots;
};

const Booking = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    message: '',
  });
  const [dateObj, setDateObj] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const availableSlots = useMemo(() => {
    if (!dateObj) return [];
    return getSlotsForDay(dayNames[dateObj.getDay()]);
  }, [dateObj]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (d) => {
    if (!d) return;
    setDateObj(d);
    setForm((prev) => ({
      ...prev,
      date: format(d, 'yyyy-MM-dd'),
      time: '', // reset time when date changes
    }));
    setDateOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.date || !form.time) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields including preferred date and time.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast({
        title: 'Booking Request Sent!',
        description: `Thanks ${form.name}! We'll confirm your ${format(dateObj, 'MMM d')} appointment shortly.`,
      });
      setForm({ name: '', email: '', phone: '', service: '', date: '', time: '', message: '' });
      setDateObj(null);
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or call us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 md:py-28 bg-gradient-to-b from-white via-pink-50/50 to-pink-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-pink-200 text-pink-600 rounded-full px-3 py-1 text-xs font-semibold tracking-wider mb-4">
            <CalendarCheck className="h-3.5 w-3.5" />
            APPOINTMENTS
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900">
            Book Your <span className="gradient-text">Appointment</span>
          </h2>
          <p className="mt-4 text-gray-600 text-lg">
            Fill out the form and we&apos;ll get back to you shortly
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl p-6 md:p-10 shadow-xl shadow-pink-100/70 border border-pink-100 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700 font-medium">
                Full Name *
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Jane Doe"
                className="border-pink-200 focus-visible:ring-pink-400 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                className="border-pink-200 focus-visible:ring-pink-400 h-11"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-700 font-medium">
                Phone Number *
              </Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="border-pink-200 focus-visible:ring-pink-400 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Service Interested In *</Label>
              <Select value={form.service} onValueChange={(v) => handleChange('service', v)}>
                <SelectTrigger className="border-pink-200 focus:ring-pink-400 h-11">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preferred Date & Time */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Preferred Date *</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'w-full flex items-center gap-2 h-11 px-3 rounded-md border border-pink-200 bg-white text-left text-sm hover:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-400 transition',
                      !dateObj && 'text-gray-400'
                    )}
                  >
                    <CalendarIcon className="h-4 w-4 text-pink-500" />
                    {dateObj ? format(dateObj, 'EEEE, MMM d, yyyy') : 'Pick a date'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border-pink-200" align="start">
                  <Calendar
                    mode="single"
                    selected={dateObj}
                    onSelect={handleDateSelect}
                    disabled={(day) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (day < today) return true;
                      // Disable Tuesdays (closed)
                      if (day.getDay() === 2) return true;
                      // Cap 90 days out
                      const cap = new Date();
                      cap.setDate(cap.getDate() + 90);
                      return day > cap;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Preferred Time *</Label>
              <Select
                value={form.time}
                onValueChange={(v) => handleChange('time', v)}
                disabled={!dateObj}
              >
                <SelectTrigger className="border-pink-200 focus:ring-pink-400 h-11">
                  <span className="inline-flex items-center gap-2">
                    <Clock className="h-4 w-4 text-pink-500" />
                    <SelectValue placeholder={dateObj ? 'Choose a time' : 'Pick a date first'} />
                  </span>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {availableSlots.length > 0 ? (
                    availableSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No slots available</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-700 font-medium">
              Additional Message
            </Label>
            <Textarea
              id="message"
              value={form.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Tell us about your preferred date, design ideas, etc."
              className="border-pink-200 focus-visible:ring-pink-400 min-h-[120px]"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white h-12 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all"
          >
            {submitting ? 'Sending...' : (<><Send className="mr-2 h-4 w-4" /> Submit Booking Request</>)}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default Booking;
